import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  useAuth,
} from '../../auth/AuthContext';

import PasswordVerificationModal
  from './components/PasswordVerificationModal';

import PasswordChangeModal
  from './components/PasswordChangeModal';

import {
  getMemberInfoProfileBundle,
  getSalesRepresentativeOptions,
  updateCompanyInfo,
} from '../services/profileService';

import GoogleAddressPicker
  from '../../auth/sign-up/components/GoogleAddressPicker';

import StatePage
  from '../../system/state/StatePage';

import './MemberInfoPage.css';


const KOREAN_ADMIN_GROUP_PRIORITY = [
  '국내영업 - 중부',
  '국내영업 - 남부',
  'SW Dev',
];


const GLOBAL_ADMIN_GROUP_PRIORITY = [
  'overseas sales',
];


const DEFAULT_SALES_CONTACT_EMAIL =
  'mtsolution@hanwha.com';


function getUserInformation(
  user,
) {
  return {
    userId:
      user?.userId ||
      user?.loginId ||
      user?.id ||
      '-',

    role:
      user?.role ||
      user?.userRole ||
      '-',
  };
}


function getCompanyInformation(
  user,
) {
  return {
    name:
      user?.company?.name ||
      user?.companyName ||
      '-',

    address:
      user?.company?.address ||
      user?.companyAddress ||
      '-',

    email:
      user?.company?.email ||
      user?.companyEmail ||
      '-',

    country:
      user?.company?.country ||
      user?.companyCountry ||
      '',
  };
}


function getSalesRepresentative(
  user,
) {
  return {
    name:
      user?.salesRepresentative?.name ||
      user?.salesRepresentativeName ||
      '-',

    email:
      user?.salesRepresentative?.email ||
      user?.salesRepresentativeEmail ||
      '-',
  };
}


function isValidEmail(
  email,
) {
  return (
    typeof email === 'string' &&
    email.includes('@') &&
    email !== '-'
  );
}


function normalizeEditableValue(
  value,
) {
  if (
    value === null ||
    value === undefined ||
    value === '-'
  ) {
    return '';
  }

  return String(
    value,
  );
}


function isKoreaCountry(
  country,
) {
  const normalizedCountry =
    String(
      country ||
      '',
    )
      .trim()
      .toLowerCase()
      .replace(
        /\s+/g,
        ' ',
      );

  return [
    'kr',
    'kor',
    'korea',
    'south korea',
    'republic of korea',
    'korea, republic of',
    '대한민국',
    '한국',
  ].includes(
    normalizedCountry,
  );
}


function getAdminGroup(
  representative,
) {
  return String(
    representative?.admin_group ||
    representative?.adminGroup ||
    '',
  ).trim();
}


function createSalesRepresentativeOptions(
  representatives,
  country,
) {
  const isKorea =
    isKoreaCountry(
      country,
    );

  const groupPriority =
    isKorea
      ? KOREAN_ADMIN_GROUP_PRIORITY
      : GLOBAL_ADMIN_GROUP_PRIORITY;

  return representatives
    .filter(
      (
        representative,
      ) =>
        groupPriority.includes(
          getAdminGroup(
            representative,
          ),
        ),
    )
    .sort(
      (
        first,
        second,
      ) => {
        const firstGroupIndex =
          groupPriority.indexOf(
            getAdminGroup(
              first,
            ),
          );

        const secondGroupIndex =
          groupPriority.indexOf(
            getAdminGroup(
              second,
            ),
          );

        if (
          firstGroupIndex !==
          secondGroupIndex
        ) {
          return (
            firstGroupIndex -
            secondGroupIndex
          );
        }

        return String(
          first?.name ||
          '',
        ).localeCompare(
          String(
            second?.name ||
            '',
          ),
          isKorea
            ? 'ko'
            : 'en',
        );
      },
    )
    .map(
      (
        representative,
      ) => {
        const id =
          representative?.id;

        if (
          id === undefined ||
          id === null ||
          id === ''
        ) {
          return null;
        }

        const name =
          String(
            representative?.name ||
            '',
          ).trim();

        const email =
          String(
            representative?.email ||
            '',
          ).trim();

        const adminGroup =
          getAdminGroup(
            representative,
          );

        let label =
          name;

        if (
          adminGroup &&
          email
        ) {
          label =
            `[${adminGroup}] ${name} (${email})`;
        } else if (
          adminGroup
        ) {
          label =
            `[${adminGroup}] ${name}`;
        } else if (
          email
        ) {
          label =
            `${name} (${email})`;
        }

        return {
          value:
            String(
              id,
            ),

          label,

          managerSeq:
            id,

          managerName:
            name,

          managerEmail:
            email,

          managerPhone:
            representative?.phone ||
            '',

          adminGroup,
        };
      },
    )
    .filter(
      Boolean,
    );
}


export default function MemberInfoPage() {
  const {
    t,
  } =
    useTranslation(
      'app',
    );

  const {
    user,
    isLocalAuth,
  } =
    useAuth();


  const [
    profileState,
    setProfileState,
  ] =
    useState({
      status:
        'loading',

      member:
        null,

      company:
        null,

      salesRepresentative:
        null,

      error:
        null,
    });


  const [
    isEditing,
    setIsEditing,
  ] =
    useState(
      false,
    );


  const [
    isSaving,
    setIsSaving,
  ] =
    useState(
      false,
    );


  const [
    isAddressPickerOpen,
    setIsAddressPickerOpen,
  ] =
    useState(
      false,
    );


  const [
    salesRepresentativeList,
    setSalesRepresentativeList,
  ] =
    useState(
      [],
    );


  const [
    isLoadingSalesRepresentatives,
    setIsLoadingSalesRepresentatives,
  ] =
    useState(
      false,
    );


  const [
    editForm,
    setEditForm,
  ] =
    useState({
      email:
        '',

      address:
        '',

      country:
        '',

      position:
        null,

      placeID:
        '',

      managerSeq:
        '',
    });


  const [
    editErrors,
    setEditErrors,
  ] =
    useState({});


  const [
    updateMessage,
    setUpdateMessage,
  ] =
    useState({
      type:
        '',

      text:
        '',
    });


  /*
   * 회원정보 수정 전에
   * 현재 비밀번호를 확인하는 모달
   */
  const [
    isPasswordModalOpen,
    setIsPasswordModalOpen,
  ] =
    useState(
      false,
    );


  /*
   * 비밀번호 변경 모달
   */
  const [
    isPasswordChangeModalOpen,
    setIsPasswordChangeModalOpen,
  ] =
    useState(
      false,
    );


  const loadProfile =
    async () => {
      const {
        member,
        company,
        salesRepresentative,
      } =
        await getMemberInfoProfileBundle();

      setProfileState({
        status:
          'ready',

        member,

        company,

        salesRepresentative,

        error:
          null,
      });

      return {
        member,
        company,
        salesRepresentative,
      };
    };


  useEffect(
    () => {
      if (
        isLocalAuth
      ) {
        setProfileState({
          status:
            'ready',

          member:
            null,

          company:
            null,

          salesRepresentative:
            null,

          error:
            null,
        });

        return undefined;
      }

      let isActive =
        true;

      const load =
        async () => {
          setProfileState(
            (
              previousState,
            ) => ({
              ...previousState,

              status:
                'loading',

              error:
                null,
            }),
          );

          try {
            const {
              member,
              company,
              salesRepresentative,
            } =
              await getMemberInfoProfileBundle();

            if (
              !isActive
            ) {
              return;
            }

            setProfileState({
              status:
                'ready',

              member,

              company,

              salesRepresentative,

              error:
                null,
            });
          } catch (
            error
          ) {
            if (
              !isActive
            ) {
              return;
            }

            setProfileState({
              status:
                'error',

              member:
                null,

              company:
                null,

              salesRepresentative:
                null,

              error,
            });
          }
        };

      load();

      return () => {
        isActive =
          false;
      };
    },
    [
      isLocalAuth,
    ],
  );


  const resolvedUser =
    useMemo(
      () => {
        if (
          isLocalAuth
        ) {
          return user;
        }

        return {
          ...user,

          ...(
            profileState.member ||
            {}
          ),

          company:
            profileState.company ||
            user?.company ||
            null,

          salesRepresentative:
            profileState.salesRepresentative ||
            user?.salesRepresentative ||
            null,
        };
      },
      [
        isLocalAuth,
        user,
        profileState.member,
        profileState.company,
        profileState.salesRepresentative,
      ],
    );


  const userInformation =
    getUserInformation(
      resolvedUser,
    );


  const companyInformation =
    getCompanyInformation(
      resolvedUser,
    );


  const salesRepresentative =
    getSalesRepresentative(
      resolvedUser,
    );


  const visibleSalesRepresentativeEmail =
    String(
      salesRepresentative.email ||
      '',
    )
      .trim()
      .toLowerCase() ===
    DEFAULT_SALES_CONTACT_EMAIL
      ? '-'
      : salesRepresentative.email;


  const salesRepresentativeOptions =
    useMemo(
      () =>
        createSalesRepresentativeOptions(
          salesRepresentativeList,
          editForm.country,
        ),
      [
        salesRepresentativeList,
        editForm.country,
      ],
    );


  /*
   * 수정 모드 진입 시
   * 담당 영업사원 목록 조회
   */
  useEffect(
    () => {
      if (
        !isEditing
      ) {
        return undefined;
      }

      let mounted =
        true;

      const loadSalesRepresentatives =
        async () => {
          setIsLoadingSalesRepresentatives(
            true,
          );

          try {
            const list =
              await getSalesRepresentativeOptions();

            if (
              !mounted
            ) {
              return;
            }

            setSalesRepresentativeList(
              Array.isArray(
                list,
              )
                ? list
                : [],
            );
          } catch (
            error
          ) {
            console.error(
              '[MemberInfoPage] Failed to load sales representatives:',
              error,
            );

            if (
              mounted
            ) {
              setSalesRepresentativeList(
                [],
              );

              setUpdateMessage({
                type:
                  'error',

                text:
                  t(
                    'memberedit.salesRepresentativeLoadFailed',
                  ),
              });
            }
          } finally {
            if (
              mounted
            ) {
              setIsLoadingSalesRepresentatives(
                false,
              );
            }
          }
        };

      loadSalesRepresentatives();

      return () => {
        mounted =
          false;
      };
    },
    [
      isEditing,
      t,
    ],
  );


  /*
   * 현재 담당 영업사원 자동 선택
   */
  useEffect(
    () => {
      if (
        !isEditing ||
        isLoadingSalesRepresentatives
      ) {
        return;
      }

      const selectedManagerExists =
        salesRepresentativeOptions.some(
          (
            option,
          ) =>
            String(
              option.value,
            ) ===
            String(
              editForm.managerSeq,
            ),
        );

      if (
        editForm.managerSeq &&
        selectedManagerExists
      ) {
        return;
      }

      const currentRepresentative =
        salesRepresentativeOptions.find(
          (
            option,
          ) =>
            String(
              option.managerEmail ||
              '',
            )
              .trim()
              .toLowerCase() ===
            String(
              salesRepresentative.email ||
              '',
            )
              .trim()
              .toLowerCase(),
        );

      setEditForm(
        (
          previous,
        ) => {
          const nextManagerSeq =
            currentRepresentative
              ? currentRepresentative.value
              : '';

          if (
            previous.managerSeq ===
            nextManagerSeq
          ) {
            return previous;
          }

          return {
            ...previous,

            managerSeq:
              nextManagerSeq,
          };
        },
      );
    },
    [
      isEditing,
      isLoadingSalesRepresentatives,
      salesRepresentativeOptions,
      salesRepresentative.email,
      editForm.managerSeq,
    ],
  );


  const openPasswordVerification =
    () => {
      setUpdateMessage({
        type:
          '',

        text:
          '',
      });

      setIsPasswordModalOpen(
        true,
      );
    };


  /*
   * 기존 회원정보 수정 모드
   */
  const startEditMode =
    () => {
      setEditForm({
        email:
          normalizeEditableValue(
            companyInformation.email,
          ),

        address:
          normalizeEditableValue(
            companyInformation.address,
          ),

        country:
          companyInformation.country ||
          '',

        position:
          null,

        placeID:
          '',

        managerSeq:
          '',
      });

      setEditErrors(
        {},
      );

      setUpdateMessage({
        type:
          '',

        text:
          '',
      });

      setIsEditing(
        true,
      );
    };


  const handleCancelEdit =
    () => {
      setIsEditing(
        false,
      );

      setIsAddressPickerOpen(
        false,
      );

      setSalesRepresentativeList(
        [],
      );

      setEditErrors(
        {},
      );

      setUpdateMessage({
        type:
          '',

        text:
          '',
      });
    };


  const handleEditChange =
    (
      fieldName,
      value,
    ) => {
      setEditForm(
        (
          previous,
        ) => ({
          ...previous,

          [
            fieldName
          ]:
            value,
        }),
      );

      setEditErrors(
        (
          previous,
        ) => {
          const next = {
            ...previous,
          };

          delete next[
            fieldName
          ];

          return next;
        },
      );
    };


  const validateEditForm =
    () => {
      const nextErrors =
        {};

      if (
        !editForm.email.trim()
      ) {
        nextErrors.email =
          t(
            'memberedit.validation.companyEmailRequired',
          );
      } else if (
        !isValidEmail(
          editForm.email.trim(),
        )
      ) {
        nextErrors.email =
          t(
            'memberedit.validation.companyEmailInvalid',
          );
      }

      if (
        !editForm.address.trim()
      ) {
        nextErrors.address =
          t(
            'memberedit.validation.companyAddressRequired',
          );
      }

      if (
        !editForm.managerSeq
      ) {
        nextErrors.managerSeq =
          t(
            'memberedit.validation.salesRepresentativeRequired',
          );
      }

      setEditErrors(
        nextErrors,
      );

      return (
        Object.keys(
          nextErrors,
        ).length ===
        0
      );
    };


  const handleSaveEdit =
    async () => {
      if (
        !validateEditForm()
      ) {
        return;
      }

      const selectedManager =
        salesRepresentativeOptions.find(
          (
            option,
          ) =>
            String(
              option.value,
            ) ===
            String(
              editForm.managerSeq,
            ),
        );

      if (
        !selectedManager
      ) {
        setEditErrors(
          (
            previous,
          ) => ({
            ...previous,

            managerSeq:
              t(
                'memberedit.validation.salesRepresentativeRequired',
              ),
          }),
        );

        return;
      }

      setIsSaving(
        true,
      );

      setUpdateMessage({
        type:
          '',

        text:
          '',
      });

      try {
        const updateData = {
          email:
            editForm.email.trim(),

          address:
            editForm.address.trim(),

          country:
            editForm.country,

          managerSeq:
            selectedManager.managerSeq,
        };

        if (
          editForm.position
        ) {
          updateData.position =
            editForm.position;
        }

        if (
          editForm.placeID
        ) {
          updateData.placeID =
            editForm.placeID;
        }

        await updateCompanyInfo(
          updateData,
        );

        await loadProfile();

        setIsEditing(
          false,
        );

        setSalesRepresentativeList(
          [],
        );

        setUpdateMessage({
          type:
            'success',

          text:
            t(
              'memberedit.updateSuccess',
            ),
        });
      } catch (
        error
      ) {
        console.error(
          '[MemberInfoPage] Failed to update member information:',
          error,
        );

        setUpdateMessage({
          type:
            'error',

          text:
            error?.message ||
            t(
              'memberedit.updateFailed',
            ),
        });
      } finally {
        setIsSaving(
          false,
        );
      }
    };


  if (
    !isLocalAuth &&
    profileState.status ===
    'loading'
  ) {
    return (
      <div
        className="memberInfo-page"
      >
        <section
          className="memberInfo-information-section"
        >
          <header
            className="memberInfo-section-heading"
          >
            <h2>
              {t(
                'memberedit.basicInformation',
              )}
            </h2>

            <p>
              {t(
                'state.loading',
              )}
            </p>
          </header>
        </section>
      </div>
    );
  }


  if (
    !isLocalAuth &&
    profileState.status ===
    'error'
  ) {
    return (
      <StatePage
        icon="warning"
        titleKey="state.dataError.title"
        descriptionKey="state.dataError.description"
      />
    );
  }


  return (
    <div
      className="memberInfo-page"
    >
      {isLocalAuth && (
        <div
          className="memberInfo-local-notice"
        >
          <strong>
            {t(
              'memberedit.localMode',
            )}
          </strong>

          <span>
            {t(
              'memberedit.localInformationNotice',
            )}
          </span>
        </div>
      )}


      {updateMessage.text && (
        <div
          className={
            updateMessage.type ===
            'success'
              ? 'memberInfo-update-message is-success'
              : 'memberInfo-update-message is-error'
          }
        >
          {
            updateMessage.text
          }
        </div>
      )}


      <section
        className="memberInfo-information-section"
      >
        <header
          className="memberInfo-section-heading"
        >
          <div
            className="memberInfo-section-heading-row"
          >
            <div
              className="memberInfo-section-heading-text"
            >
              <h2>
                {t(
                  'memberedit.basicInformation',
                )}
              </h2>

              <p>
                {t(
                  'memberedit.basicInformationDescription',
                )}
              </p>
            </div>


            {!isLocalAuth && (
              <div
                className="memberInfo-header-actions"
              >
                {!isEditing ? (
                  <>
                    <button
                      type="button"
                      className="memberInfo-edit-button"
                      onClick={
                        openPasswordVerification
                      }
                    >
                      {t(
                        'memberedit.edit',
                      )}
                    </button>

                    <button
                      type="button"
                      className="memberInfo-password-change-button"
                      onClick={() => {
                        setUpdateMessage({
                          type:
                            '',

                          text:
                            '',
                        });

                        setIsPasswordChangeModalOpen(
                          true,
                        );
                      }}
                    >
                      {t(
                        'memberedit.passwordChange.title'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="memberInfo-cancel-button"
                      onClick={
                        handleCancelEdit
                      }
                      disabled={
                        isSaving
                      }
                    >
                      {t(
                        'common.cancel',
                      )}
                    </button>

                    <button
                      type="button"
                      className="memberInfo-save-button"
                      onClick={
                        handleSaveEdit
                      }
                      disabled={
                        isSaving ||
                        isLoadingSalesRepresentatives
                      }
                    >
                      {isSaving
                        ? t(
                            'common.saving',
                          )
                        : t(
                            'common.save',
                          )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </header>


        <div
          className="memberInfo-information-grid"
        >
          <article>
            <span>
              {t(
                'memberedit.role',
              )}
            </span>

            <strong>
              {userInformation.role ===
              'M001'
                ? t(
                    'memberedit.roleAdmin',
                  )
                : t(
                    'memberedit.roleUser',
                  )}
            </strong>
          </article>


          <article>
            <span>
              {t(
                'memberedit.companyName',
              )}
            </span>

            <strong>
              {
                companyInformation.name
              }
            </strong>
          </article>


          <article>
            <span>
              {t(
                'memberedit.userId',
              )}
            </span>

            <strong>
              {
                userInformation.userId
              }
            </strong>
          </article>


          <article>
            <span>
              {t(
                'memberedit.companyEmail',
              )}
            </span>

            {isEditing ? (
              <>
                <input
                  type="email"
                  className={
                    editErrors.email
                      ? 'memberInfo-edit-input has-error'
                      : 'memberInfo-edit-input'
                  }
                  value={
                    editForm.email
                  }
                  onChange={(
                    event,
                  ) =>
                    handleEditChange(
                      'email',
                      event.target.value,
                    )
                  }
                />

                {editErrors.email && (
                  <small
                    className="memberInfo-edit-error"
                  >
                    {
                      editErrors.email
                    }
                  </small>
                )}
              </>
            ) : (
              isValidEmail(
                companyInformation.email,
              ) ? (
                <a
                  href={
                    `mailto:${companyInformation.email}`
                  }
                >
                  {
                    companyInformation.email
                  }
                </a>
              ) : (
                <strong>
                  {
                    companyInformation.email
                  }
                </strong>
              )
            )}
          </article>


          <article
            className="is-wide"
          >
            <span>
              {t(
                'memberedit.companyAddress',
              )}
            </span>

            {isEditing ? (
              <>
                <div
                  className="memberInfo-address-input-row"
                >
                  <input
                    type="text"
                    className={
                      editErrors.address
                        ? 'memberInfo-edit-input has-error'
                        : 'memberInfo-edit-input'
                    }
                    value={
                      editForm.address
                    }
                    readOnly
                  />

                  <button
                    type="button"
                    className="memberInfo-map-button"
                    onClick={
                      () =>
                        setIsAddressPickerOpen(
                          true,
                        )
                    }
                  >
                    {t(
                      'signup.addressPicker.open',
                    )}
                  </button>
                </div>

                {editErrors.address && (
                  <small
                    className="memberInfo-edit-error"
                  >
                    {
                      editErrors.address
                    }
                  </small>
                )}
              </>
            ) : (
              <strong>
                {
                  companyInformation.address
                }
              </strong>
            )}
          </article>
        </div>
      </section>


      <section
        className="memberInfo-information-section"
      >
        <header
          className="memberInfo-section-heading"
        >
          <h2>
            {t(
              'memberedit.salesRepresentative',
            )}
          </h2>

          <p>
            {t(
              'memberedit.salesRepresentativeDescription',
            )}
          </p>
        </header>


        {isEditing ? (
          <div
            className="memberInfo-sales-edit-card"
          >
            <select
              id="memberInfo-sales-representative"
              className={
                editErrors.managerSeq
                  ? 'memberInfo-sales-select has-error'
                  : 'memberInfo-sales-select'
              }
              value={
                editForm.managerSeq
              }
              disabled={
                isLoadingSalesRepresentatives
              }
              onChange={(
                event,
              ) =>
                handleEditChange(
                  'managerSeq',
                  event.target.value,
                )
              }
            >
              <option
                value=""
              >
                {isLoadingSalesRepresentatives
                  ? t(
                      'signup.fields.loadingSalesRepresentative',
                    )
                  : t(
                      'signup.fields.selectSalesRepresentative',
                    )}
              </option>

              {salesRepresentativeOptions.map(
                (
                  option,
                ) => (
                  <option
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {
                      option.label
                    }
                  </option>
                ),
              )}
            </select>

            {editErrors.managerSeq && (
              <small
                className="memberInfo-edit-error"
              >
                {
                  editErrors.managerSeq
                }
              </small>
            )}
          </div>
        ) : (
          <div
            className="memberInfo-information-grid"
          >
            <article>
              <span>
                {t(
                  'memberedit.salesRepresentativeName',
                )}
              </span>

              <strong>
                {
                  salesRepresentative.name
                }
              </strong>
            </article>

            <article>
              <span>
                {t(
                  'memberedit.salesRepresentativeEmail',
                )}
              </span>

              {isValidEmail(
                visibleSalesRepresentativeEmail,
              ) ? (
                <a
                  href={
                    `mailto:${visibleSalesRepresentativeEmail}`
                  }
                >
                  {
                    visibleSalesRepresentativeEmail
                  }
                </a>
              ) : (
                <strong>
                  {
                    visibleSalesRepresentativeEmail
                  }
                </strong>
              )}
            </article>
          </div>
        )}
      </section>


      <GoogleAddressPicker
        open={
          isAddressPickerOpen
        }
        initialAddress={
          editForm.address
        }
        onClose={
          () =>
            setIsAddressPickerOpen(
              false,
            )
        }
        onSelect={(
          mapInfo,
        ) => {
          setEditForm(
            (
              previous,
            ) => ({
              ...previous,

              address:
                mapInfo.address ||
                '',

              country:
                mapInfo.country ||
                '',

              position:
                mapInfo.position ||
                null,

              placeID:
                mapInfo.placeID ||
                '',

              managerSeq:
                '',
            }),
          );

          setEditErrors(
            (
              previous,
            ) => {
              const next = {
                ...previous,
              };

              delete next.address;
              delete next.managerSeq;

              return next;
            },
          );

          setIsAddressPickerOpen(
            false,
          );
        }}
      />


      <PasswordVerificationModal
        open={
          isPasswordModalOpen
        }
        onClose={() =>
          setIsPasswordModalOpen(
            false,
          )
        }
        onVerified={() => {
          setIsPasswordModalOpen(
            false,
          );

          startEditMode();
        }}
      />


      <PasswordChangeModal
        open={
          isPasswordChangeModalOpen
        }
        onClose={() =>
          setIsPasswordChangeModalOpen(
            false,
          )
        }
        onChanged={() => {
          setIsPasswordChangeModalOpen(
            false,
          );

          setUpdateMessage({
            type:
              'success',

            text:
              t(
                'memberedit.passwordChange.changeSuccess'
              ),
          });
        }}
      />
    </div>
  );
}
